import { useLayoutEffect, useRef, type RefObject } from "react";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * Short, soft fade on an existing element whenever `watch` changes (page,
 * tab, open record…). Nothing is remounted, so drafts and state are kept.
 * Skips the first render and respects "reduce motion".
 */
export const useFadeOnChange = <T extends HTMLElement>(
  watch: unknown,
  ref?: RefObject<T | null>,
) => {
  const ownRef = useRef<T>(null);
  const target = ref ?? ownRef;
  const first = useRef(true);

  useLayoutEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const node = target.current;
    if (!node || prefersReducedMotion() || typeof node.animate !== "function") {
      return;
    }
    // Opacity only (no movement, so nothing jumps); starting from 35%
    // avoids a blank flash.
    node.animate([{ opacity: 0.35 }, { opacity: 1 }], {
      duration: 220,
      easing: "cubic-bezier(0.4, 0, 0.2, 1)",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch]);

  return target;
};
